import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { InstallationDetailsStore } from '@store';
import { DocumentType, EquipmentDocumentResponse } from '@vi-iot/client';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ServiceInstructinHelper } from './service-instruction.helper';

@Component({
  selector: 'app-service-instruction',
  templateUrl: './service-instruction.component.html',
  styleUrls: ['./service-instruction.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceInstructionComponent implements OnInit, OnDestroy {
  documentGroups: ServiceInstructinHelper.DocumentGroup[] = [];
  selectedDocument$: BehaviorSubject<EquipmentDocumentResponse | null> = new BehaviorSubject<EquipmentDocumentResponse | null>(
    null
  );
  hasDocuments = false;

  private ngUnsubscribe$ = new Subject<void>();

  constructor(
    protected installationDetailsStore: InstallationDetailsStore,
    private cdf: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.installationDetailsStore.getServiceInstruction([
      DocumentType.SERVICE_INSTRUCTIONS,
      DocumentType.DATASHEET,
      DocumentType.PLANNING_INSTRUCTIONS,
      DocumentType.CONNECTION_AND_WIRING_DIAGRAM,
    ]);

    this.installationDetailsStore.serviceInstruction$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((documents: EquipmentDocumentResponse[]) => {
        this.documentGroups = this.groupDocumentsByType(documents);
        this.hasDocuments = this.documentGroups.some((group) => group.documents.length > 0);
        this.cdf.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  onSelectDocument(document: EquipmentDocumentResponse): void {
    this.selectedDocument$.next(null);

    // COMMENT: Using setTimeout to ensure the UI updates correctly
    setTimeout(() => {
      this.selectedDocument$.next(document);
    });
  }

  private groupDocumentsByType(
    documents: EquipmentDocumentResponse[]
  ): ServiceInstructinHelper.DocumentGroup[] {
    if (!documents || documents.length === 0) {
      return [];
    }

    const groupMap = new Map<string, ServiceInstructinHelper.DocumentGroup>();

    documents.forEach((document) => {
      const { documentTypeName } = document;

      if (!groupMap.has(documentTypeName)) {
        groupMap.set(documentTypeName, {
          documentTypeName,
          documents: [],
        });
      }

      groupMap.get(documentTypeName)?.documents.push(document);
    });

    const groups = Array.from(groupMap.values()).sort((a, b) =>
      a.documentTypeName.localeCompare(b.documentTypeName)
    );

    groups.forEach((group) => {
      group.documents.sort((a, b) => a.name.localeCompare(b.name));
    });

    return groups;
  }
}
